import {
  fetchPlaylistRecommendationTagsApi,
  createPlaylistRecommendationTagApi,
} from "@src/apis/playlistRecommendationTagsApi";
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { QUERY_KEYS } from "./queryKeys";
import { AxiosResponse } from "axios";
import { IPlaylistRecommendationTag } from "@src/types/playlistRecommendationTag";
import { ApiResponse } from "@src/types/httpApi";

export const useFetchPlaylistRecommendationTagsInfiniteQuery = ({
  searchText,
  pageSize = 20,
}: {
  searchText?: string;
  pageSize?: number;
}) =>
  useInfiniteQuery<
    AxiosResponse<ApiResponse<IPlaylistRecommendationTag[]>>,
    Error
  >({
    queryKey: [QUERY_KEYS.GET_PLAYLIST_RECOMMENDATION_TAGS, searchText],
    queryFn: async ({ pageParam = 1 }) =>
      fetchPlaylistRecommendationTagsApi({
        searchText,
        page: pageParam as number,
        pageSize,
      }),
    initialPageParam: 1,
    getNextPageParam: (
      lastPage:
        | AxiosResponse<ApiResponse<IPlaylistRecommendationTag[]>>
        | undefined
    ) => {
      if (!lastPage || !lastPage?.data?.meta?.pagination) {
        return undefined;
      }
      try {
        const { page: currentPage, pageCount: totalPages } =
          lastPage.data.meta.pagination;
        return currentPage < totalPages ? currentPage + 1 : undefined;
      } catch {
        return undefined;
      }
    },
    staleTime: 1000 * 60 * 60, // 1 hour
    gcTime: 1000 * 60 * 60 * 24, // 24 hours
  });

export const useCreatePlaylistRecommendationTagMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [QUERY_KEYS.CREATE_PLAYLIST_RECOMMENDATION_TAG],
    mutationFn: ({ title }: { title: string }) =>
      createPlaylistRecommendationTagApi({ title }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_PLAYLIST_RECOMMENDATION_TAGS],
      });
    },
  });
};
